import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button } from '@artosera/ui';
import { TableauDefilant } from '../../../components/TableauDefilant.js';
import {
  estNaturePivot,
  lireRapportEssaiMorceaux,
  type NaturePivot,
} from '../../../lib/correspondance.js';
import { morceauxLances } from '../../../lib/reglesEdition.js';
import { essayerLot, type LotReprise } from '../../../lib/reprise.js';
import tableStyles from '../../../styles/table.module.css';
import styles from './correspondance/Correspondance.module.css';
import { TON_ETAT_LOT, messageReprise } from './messages.js';
import { EcartementBloc } from './plan/EcartementBloc.js';
import reprise from './Reprise.module.css';

/**
 * Les morceaux d'un lot tiré d'un dépôt, leur essai et le lancement : un lot
 * ordinaire chacun, qui s'essaie ensemble et se lance depuis sa propre fiche.
 * Partagé par la correspondance (morceaux de pivot) et les images (morceaux du
 * plan) ; `textes` choisit le jeu de phrases (`correspondance` ou `images`).
 */
export function MorceauxEssai({
  idGalerie,
  lot,
  peutAgir,
  actif,
  textes,
  onLot,
  onEssaiLance,
  onTelecharger,
}: {
  idGalerie: string;
  lot: LotReprise;
  peutAgir: boolean;
  /** Les morceaux existent et sont à jour : l'essai se lit. */
  actif: boolean;
  textes: 'correspondance' | 'images';
  onLot: (lot: LotReprise) => void;
  onEssaiLance: () => void;
  /** Un morceau de pivot se télécharge en CSV ; absent : pas de bouton. */
  onTelecharger?: (cible: { nature: NaturePivot; morceau: number }) => void;
}): JSX.Element | null {
  const { t } = useTranslation('console');
  const client = useQueryClient();
  const [erreur, setErreur] = useState<string | null>(null);
  const morceaux = lot.morceaux?.liste ?? [];
  const essayes = morceaux.filter((morceau) => morceau.etat !== 'depose').length;
  const lances = morceauxLances(lot);

  const essai = useQuery({
    queryKey: [
      'console',
      'galerie',
      idGalerie,
      'reprise',
      'lot',
      lot.id,
      'essai-morceaux',
      essayes,
      lot.revision,
    ],
    queryFn: () => lireRapportEssaiMorceaux(idGalerie, lot.id),
    enabled: actif && essayes > 0,
  });
  const essayer = useMutation({
    mutationFn: () => essayerLot(idGalerie, lot.id),
    onSuccess: (suite) => {
      setErreur(null);
      onEssaiLance();
      onLot(suite);
    },
    onError: (cause) => {
      setErreur(messageReprise(t, cause, textes === 'images' ? 'images' : undefined));
      void client.invalidateQueries({
        queryKey: ['console', 'galerie', idGalerie, 'reprise', 'lot', lot.id],
      });
    },
  });

  if (!actif || morceaux.length === 0) return null;
  const naturesDuLot = new Set(morceaux.map((morceau) => morceau.nature));

  return (
    <div className={reprise.pile}>
      <h3 className={reprise.sousTitre}>
        {t(`${textes}.morceaux.titre`, { count: morceaux.length })}
      </h3>
      <TableauDefilant libelle={t(`${textes}.morceaux.titre`, { count: morceaux.length })} empile>
        <table className={`${tableStyles.table} ${reprise.table}`}>
          <thead>
            <tr>
              <th scope="col">{t('correspondance.morceaux.colonnes.morceau')}</th>
              <th scope="col">{t(`${textes}.morceaux.colonnes.lignes`)}</th>
              <th scope="col">{t('correspondance.morceaux.colonnes.etat')}</th>
              <th scope="col">{t('correspondance.morceaux.colonnes.action')}</th>
            </tr>
          </thead>
          <tbody>
            {morceaux.map((morceau) => (
              <tr key={morceau.id}>
                <td data-label={t('correspondance.morceaux.colonnes.morceau')}>
                  {t('correspondance.morceaux.nom', {
                    nature: t(`reprise.natures.${morceau.nature}`),
                    rang: morceau.rang,
                  })}
                </td>
                <td data-label={t(`${textes}.morceaux.colonnes.lignes`)}>{morceau.lignes}</td>
                <td data-label={t('correspondance.morceaux.colonnes.etat')}>
                  <Badge tone={TON_ETAT_LOT[morceau.etat]}>
                    {morceau.etat === 'depose'
                      ? t('correspondance.morceaux.nonEssaye')
                      : t(`reprise.etats.${morceau.etat}`)}
                  </Badge>
                </td>
                <td data-label={t('correspondance.morceaux.colonnes.action')}>
                  <Link
                    className={reprise.lienLot}
                    to={`?lot=${encodeURIComponent(morceau.id)}`}
                    aria-label={t('correspondance.morceaux.ouvrir', {
                      nature: t(`reprise.natures.${morceau.nature}`),
                      rang: morceau.rang,
                    })}
                  >
                    {t('correspondance.morceaux.ouvrirCourt')}
                  </Link>
                  {onTelecharger && estNaturePivot(morceau.nature) ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('correspondance.morceaux.telecharger', {
                        nature: t(`reprise.natures.${morceau.nature}`),
                        rang: morceau.rang,
                      })}
                      onClick={() =>
                        onTelecharger({
                          nature: morceau.nature as NaturePivot,
                          morceau: morceau.rang,
                        })
                      }
                    >
                      {t('correspondance.morceaux.csv')}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableauDefilant>
      {naturesDuLot.size > 1 ? (
        <p className={reprise.discret}>{t('correspondance.morceaux.scission')}</p>
      ) : null}

      <div className={styles.essaiMorceaux}>
        <h3 className={reprise.sousTitre}>{t(`${textes}.essai.titre`)}</h3>
        <p className={reprise.texte}>{t(`${textes}.essai.texte`)}</p>
        {erreur ? (
          <p className={reprise.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
        {peutAgir ? (
          <div className={reprise.actions}>
            <Button
              variant="secondary"
              loading={essayer.isPending}
              disabled={lances === morceaux.length}
              onClick={() => essayer.mutate()}
            >
              {essayes === 0 ? t(`${textes}.essai.essayer`) : t(`${textes}.essai.essayerDeNouveau`)}
            </Button>
          </div>
        ) : null}
        {essai.isSuccess && essai.data.essayes > 0 ? (
          <div className={reprise.rapport}>
            <h4>
              {t(`${textes}.essai.rapport`, {
                essayes: essai.data.essayes,
                total: essai.data.morceaux,
              })}
            </h4>
            <dl className={reprise.compteurs}>
              <div>
                <dt>{t(`${textes}.essai.total`)}</dt>
                <dd>{essai.data.total}</dd>
              </div>
              <div>
                <dt>{t(`${textes}.essai.crees`)}</dt>
                <dd>{essai.data.crees}</dd>
              </div>
              <div data-refus={essai.data.refuses > 0 ? 'true' : undefined}>
                <dt>{t('correspondance.apercu.refusees')}</dt>
                <dd>{essai.data.refuses}</dd>
              </div>
            </dl>
            {essai.data.erreurs.length === 0 ? (
              <p className={reprise.discret}>{t('correspondance.essai.sansRefus')}</p>
            ) : (
              <div className={reprise.lignes}>
                <h5 className={reprise.sousTitre}>{t('correspondance.essai.refusParMessage')}</h5>
                <ul className={styles.groupesMessages}>
                  {essai.data.erreurs.map((groupe) => (
                    <li key={groupe.message}>
                      <strong>{groupe.message}</strong>
                      <span className={reprise.discret}>
                        {t(`${textes}.essai.occurrences`, { count: groupe.nombre })}
                      </span>
                      <details>
                        <summary>{t('correspondance.essai.voirLignes')}</summary>
                        <ul className={styles.listeSimple}>
                          {groupe.occurrences.slice(0, 8).map((occurrence, rang) => (
                            <li key={rang}>
                              {t(`${textes}.essai.occurrence`, {
                                morceau: occurrence.morceau
                                  ? t('correspondance.morceaux.nom', {
                                      nature: t(`reprise.natures.${occurrence.morceau.nature}`),
                                      rang: occurrence.morceau.rang,
                                    })
                                  : '',
                                ligne: occurrence.ligne,
                                libelle: occurrence.libelle,
                              })}
                            </li>
                          ))}
                        </ul>
                      </details>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <EcartementBloc idGalerie={idGalerie} lot={lot} peutAgir={peutAgir} onLot={onLot} />

      <div className={styles.lancement}>
        <h3 className={reprise.sousTitre}>{t('correspondance.lancement.titre')}</h3>
        <p className={reprise.texte}>{t('correspondance.lancement.texte')}</p>
        <p className={reprise.avertissement} role="note">
          {t(`${textes}.lancement.parent`)}
        </p>
        <p className={reprise.discret} aria-live="polite">
          {t('correspondance.lancement.avancement', { lances, total: morceaux.length })}
        </p>
      </div>
    </div>
  );
}
